import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * RotatingTagline , animated cycling headline for dark brand panels.
 * Each tagline has a `main` (white) and optional `accent` (orange) phrase
 * so the pair reads with high contrast on our indigo backgrounds.
 *
 * Props:
 *   taglines: [{ main: string, accent?: string }]
 *   intervalMs: rotation speed (default 4500)
 */
const DEFAULT_TAGLINES = [
  { main: "Every case gets a fair hearing.", accent: "Every decision leaves a trail." },
  { main: "Anonymous by design.", accent: "Confidential by law." },
  { main: "Human judgment stays at the wheel.", accent: "AI keeps the record honest." },
  { main: "Fingerprinted evidence.", accent: "Approval-gated findingsList." },
  { main: "Built for HR, engineered for compliance.", accent: "Trusted by every stakeholder." },
];

export default function RotatingTagline({
  taglines = DEFAULT_TAGLINES,
  intervalMs = 4500,
  className = "",
  testid = "rotating-tagline",
}) {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % taglines.length), intervalMs);
    return () => clearInterval(t);
  }, [taglines.length, intervalMs]);

  const t = taglines[i];

  return (
    <div className={"relative " + className} data-testid={testid} style={{ minHeight: "9.5rem" }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl md:text-5xl leading-tight font-semibold"
        >
          <div style={{ color: "#FFFFFF" }}>{t.main}</div>
          {t.accent && (
            <div style={{ color: "#E4943A" }} className="mt-1">
              {t.accent}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Progress dots */}
      <div className="mt-6 flex items-center gap-1.5">
        {taglines.map((_, idx) => (
          <span
            key={idx}
            className="h-[3px] rounded-full transition-all duration-500"
            style={{
              width: idx === i ? 28 : 10,
              background: idx === i ? "#E4943A" : "rgba(255,255,255,0.25)",
            }}
          />
        ))}
      </div>
    </div>
  );
}
